import { FC, useMemo } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { Route } from '@core/routes/Route';
import Layout from '@vms/Layout';
import Home from '@pages/Home';
import ConceptLocker from '@pages/ConceptLocker';
import ItemDetails from '@pages/ItemDetails';
import FoodsFlavors from '@pages/FoodsFlavors';
import MyConcepts from '@pages/MyConcepts';
import ConceptPage from '@pages/ConcepPage';
import ChainsChart from '@pages/ChainsChart';
import Benchmark from '@pages/Benchmark';
import Search from '@pages/Search';
import FoodWatch from '@pages/FoodWatch';
import FoodWatchItem from '@pages/FoodWatchItem';
import ChainProfile from '@pages/ChainProfile';
import { UiSettingsKey, useUserContext } from '@core/UserContext';
import SeasonalActivity from '@pages/SeasonalActivity';
import CategoryActivity from '@pages/CategoryActivity';
import ChainPerformance from "@pages/ChainPerformance";
import ChainLaunches from '@pages/ChainLaunches';
import ApplyFilters from "@pages/ApplyFilters";

const Router: FC = () => {
    const {
        hasConceptLockerAccess,
        uiSettings: { hiddenElements },
    } = useUserContext();

    const dependingRoutes = useMemo(() => [
            {
                uiSettingsKeys: [UiSettingsKey.UiHomePageViewSettings],
                routes: [
                    { path: Route.HomeViewTable, element: <Home/> },
                    { path: Route.HomeViewPeople, element: <Home/> },
                    { path: Route.HomeViewChart, element: <Home/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageSeasonalActivity],
                routes: [
                    { path: Route.SeasonalActivity, element: <SeasonalActivity/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageCategoryActivity],
                routes: [
                    { path: Route.CategoryActivity, element: <CategoryActivity/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PagePerformanceByChain],
                routes: [
                    { path: Route.ChainPerformance, element: <ChainPerformance/> },
                    { path: Route.ChainsChart, element: <ChainsChart/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageChainProfile],
                routes: [
                    { path: Route.ChainProfile, element: <ChainProfile/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageSpecificFoodFlavors],
                routes: [
                    { path: Route.FoodsFlavors, element: <FoodsFlavors/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageBenchmark],
                routes: [
                    { path: Route.Benchmark, element: <Benchmark/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageFoodWatch],
                routes: [
                    { path: Route.FoodWatch, element: <FoodWatch/> },
                    { path: Route.FoodWatchItemCreate, element: <FoodWatchItem/> },
                    { path: Route.FoodWatchItemEdit, element: <FoodWatchItem/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageConceptLocker],
                additionalCheck: hasConceptLockerAccess,
                routes: [
                    { path: Route.ConceptLocker, element: <ConceptLocker/> },
                    { path: Route.MyConcepts, element: <MyConcepts/> },
                    { path: Route.ConceptPageCreate, element: <ConceptPage/> },
                    { path: Route.ConceptPageEdit, element: <ConceptPage/> },
                ],
            },
            {
                uiSettingsKeys: [UiSettingsKey.PageConceptLocker, UiSettingsKey.UiHomePageViewSettings],
                routes: [
                    { path: Route.ConceptLockerViewTiles, element: <ConceptLocker/> },
                    { path: Route.ConceptLockerViewPeople, element: <ConceptLocker/> },
                    { path: Route.ConceptLockerViewChart, element: <ConceptLocker/> },
                ],
            },
        ],
        [hasConceptLockerAccess]
    );

    const children = useMemo(() => {
        let result = [
            { path: '*', element: <Navigate to="/"/> },
            { path: Route.Search, element: <Search/> },
            { path: Route.Home, element: <Home/> },
            { path: Route.ItemDetails, element: <ItemDetails/> },
            { path: Route.CLItemDetails, element: <ItemDetails/> },
            { path: Route.ChainLaunches, element: <ChainLaunches/> },
            { path: Route.ApplyFilters, element: <ApplyFilters/> },

        ];

        dependingRoutes.forEach(({
            uiSettingsKeys,
            additionalCheck = true,
            routes,
        }) => {
            if (uiSettingsKeys.every(key => !hiddenElements.includes(key)) && additionalCheck) {
                result = [...result, ...routes];
            }
        });

        return result;
    }, [dependingRoutes, hiddenElements]);

    const routing = useRoutes([{
        path: '/',
        element: <Layout/>,
        children,
    }]);
    return <>{routing}</>
};

export default Router;